import React, { createContext, useContext } from 'react';
import { cn } from '../utils';
import { cva, VariantProps } from 'class-variance-authority';

const BaseLayoutVariants = cva(
  `
    w-screen h-dvh
    grid gap-0`,
  {
    variants: {
      orientation: {
        // `minmax(0,1fr)`: the menu bar's content can never widen the page past the screen.
        horizontal:
          "grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr] [grid-template-areas:'menu'_'content']",
        vertical: "grid-cols-[auto_1fr] [grid-template-areas:'menu_content']"
      }
    },
    defaultVariants: {
      orientation: 'vertical'
    }
  }
);

type BaseLayoutVariantsVariantProps = VariantProps<typeof BaseLayoutVariants>;

export interface BaseLayoutProps extends BaseLayoutVariantsVariantProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface BaseLayoutMenuProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface BaseLayoutContentProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

const BaseLayoutContext = createContext<{ orientation?: 'horizontal' | 'vertical' }>({
  orientation: 'vertical'
});

const BaseLayoutRoot: React.FC<BaseLayoutProps> = ({
  children,
  className,
  orientation,
  testId
}) => {
  return (
    <BaseLayoutContext.Provider value={{ orientation: orientation ?? undefined }}>
      <div data-testid={testId} className={cn(BaseLayoutVariants({ orientation }), className)}>
        {children}
      </div>
    </BaseLayoutContext.Provider>
  );
};

BaseLayoutRoot.displayName = 'BaseLayout';

const Menu: React.FC<BaseLayoutMenuProps> = ({ children, className, testId }) => {
  const { orientation } = useContext(BaseLayoutContext);
  const isHorizontal = orientation === 'horizontal';

  return (
    <nav
      aria-label='Main navigation'
      data-testid={testId}
      className={cn(
        'bg-background-input h-full relative z-50',
        isHorizontal
          ? 'grid grid-cols-[minmax(0,1fr)] [grid-area:menu]'
          : 'hidden xl:grid xl:[grid-area:menu]',
        className
      )}
    >
      {children}
    </nav>
  );
};
Menu.displayName = 'BaseLayout.Menu';

const Content: React.FC<BaseLayoutContentProps> = ({ children, className, testId }) => (
  <main
    data-testid={testId}
    className={cn('bg-background-base [grid-area:content] overflow-hidden', className)}
  >
    {children}
  </main>
);
Content.displayName = 'BaseLayout.Content';

export const BaseLayout = Object.assign(BaseLayoutRoot, {
  Menu,
  Content
});
